import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {Button, Header, Modal, Popup, TextArea} from "semantic-ui-react";
import {parseDiff} from "react-diff-view";
import {authentication} from "../../../../../../../authentication";
import {backend} from "../../../../../../../xconvert-backend";
import {Contract, SaveContractRequest} from "../../../../../../../generated";
import {notify} from "react-notify-toast";
import {diffLines, formatLines} from "unidiff";
import {ConfigContext} from "../../../../../context/ConfigContext";
import {IfBox} from "../../../../../../style/if";
import {CustomDiff} from "../../../../configChange/configEdit/diff/CustomDiff";
import {ticketNumberInput} from "../../../../configChange/configEdit/modals/ChangeConfigModal";

export interface ResponseModalProps {
    isOpen: boolean
    onClose: () => void
    dispatch: (any) => void
}

export function SaveContractModal(props: ResponseModalProps) {
    const context = useContext(ConfigContext);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comment, setComment] = useState("");
    const [hunks, setHunks] = useState<any | null>(null);
    const [ticketNrs, setTicketNrs] = useState<string[]>([]);
    const [canBeSavedAsNew, setCanBeSavedAsNew] = useState(false);
    const [canBeUpdated, setCanBeUpdated] = useState(false);
    const [unsavedTicketNumber, setUnsavedTicketNumber] = useState<boolean>(false);

    function handleClose(reload: boolean) {
        props.dispatch(reload);
        props.onClose();
    }

    async function save(update: boolean) {
        setIsSubmitting(true);
        let reload = false;
        try {
            let auth = backend.withTokenAuthHeader(authentication.token);
            let request = {} as SaveContractRequest;
            request.companyId = context.companyId;
            if (update) {
                request.oldContract = JSON.parse(context.oldContract);
            }
            request.newContract = JSON.parse(context.selectedContract);
            request.comment = comment;
            request.ticketNrs = ticketNrs;
            let res = await backend.internalApi.saveContract(request, auth);
            if (res.successful) {
                notify.show('saved contract', 'success', 3000, '#28f751');
                context.setSelectedContract(null);
            } else {
                notify.show("ERROR: unable to save contract.", 'error', 5000, '#fc0303');

                reload = res.successful;
            }
        } catch (ex) {
            console.log(ex);
        }
        setIsSubmitting(false);
        handleClose(reload);
    }

    useEffect(() => {
        async function fetchData() {
            if (hunks == null) {
                let oldConfig = context.oldContract ?? "";
                let newConfig = context.selectedContract;
                const diffText = formatLines(diffLines(oldConfig, newConfig), {context: 3});
                const [diff] = parseDiff(diffText, {nearbySequences: 'zip'});
                setHunks(diff.hunks);
            }
            let contracts = await fetchContracts();
            checkCanBeSavedAsNew(contracts);
            checkCanBeUpdated(contracts);
        }

        fetchData();
        // eslint-disable-next-line
    }, []);

    async function fetchContracts() {
        let auth = backend.withTokenAuthHeader(authentication.token);
        let response = await backend.internalApi.fetchCompanyContracts(
            context.companyId,
            null,
            null,
            null,
            'VALID_FROM',
            "ASC",
            auth
        );
        return response.contracts;
    }

    function epoch(date: string) {
        return Date.parse(date);
    }

    function checkCanBeUpdated(contracts: Contract[]) {
        let canUpdate = true;
        let contract = JSON.parse(context.selectedContract);
        let validFrom = contract.validFrom;
        Object.values(contracts).forEach(c => {
            if (areDatesEqual(c.validFrom, validFrom)) {
                if (c._id !== contract._id)
                    canUpdate = false;
            }
        });
        setCanBeUpdated(canUpdate);
    }

    function areDatesEqual(date1: Date | null, date2: Date | null): boolean {
        let a = date1 ?? null;
        let b = date2 ?? null;
        console.log("a: ", a, " b: ", b);
        return a === b
    }

    function checkCanBeSavedAsNew(contracts: Contract[]) {
        let canSave = true;
        let validFrom = JSON.parse(context.selectedContract).validFrom;
        Object.values(contracts).forEach(c => {
            if (areDatesEqual(c.validFrom, validFrom)) {
                canSave = false;
            }
        });
        setCanBeSavedAsNew(canSave);
    }

    function cleanBackslashR(str: string): string {
        if (str) {
            return str.split("\\r").join("");
        } else {
            return "";
        }
    }

    function drawSaveButton() {
        return (
            <span>
                <Button
                    id='SaveContractModalSaveButton'
                    loading={isSubmitting}
                    disabled={!canBeSavedAsNew || unsavedTicketNumber}
                    onClick={() => save(false)}
                >
                    Save as new
                </Button>
            </span>
        );
    }

    function drawUpdateButton() {
        return (
            <span>
                <Button
                    id='UpdateContractModalUpdateButton'
                    loading={isSubmitting}
                    disabled={context.oldContract == null || !canBeUpdated || unsavedTicketNumber}
                    onClick={() => save(true)}
                >
                    update
                </Button>
            </span>
        );
    }

    function drawButtons() {
        return (
            <span>
                <IfBox shouldShow={context.oldContract == null || !canBeUpdated}>
                    <Popup content='Can not update: ValidFrom is already taken and must be unique.' trigger={
                        drawUpdateButton()
                    }/>
                </IfBox>
                <IfBox shouldShow={!(context.oldContract == null || !canBeUpdated)}>
                    {drawUpdateButton()}
                </IfBox>
                <IfBox shouldShow={!canBeSavedAsNew}>
                    <Popup content='Can not save as new: ValidFrom is already taken and must be unique.' trigger={
                        drawSaveButton()
                    }/>
                </IfBox>
                <IfBox shouldShow={canBeSavedAsNew}>
                    {drawSaveButton()}
                </IfBox>
                <Button id='CancelContractModalCancelButton' onClick={() => handleClose(false)}>Cancel</Button>
            </span>
        );
    }

    return (
        <Modal
            open={props.isOpen}
            onClose={() => handleClose(false)}
            size='fullscreen'
        >
            <Header icon='browser' content='Contract change'/>
            <Modal.Content>
                <CustomDiff
                    oldJson={cleanBackslashR(context.oldContract)}
                    newJson={cleanBackslashR(context.selectedContract)}
                />
                <h3>leave a comment</h3>
                <TextArea
                    id='CommentTextArea'
                    onChange={(event) => setComment(event.currentTarget.value)}
                    cols={200}
                />
                {ticketNumberInput(ticketNrs, setTicketNrs, setUnsavedTicketNumber)}
                {drawButtons()}
            </Modal.Content>
        </Modal>
    );
}
